import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Check,
  ArrowLeft,
  ArrowRight,
  Star,
  Phone,
  Zap
} from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();
  const BLDR_URL = process.env.REACT_APP_BLDR_URL || 'https://bldr.companeze.com';

  const monthlyPlans = [
    {
      name: 'BLDR Starter',
      price: '99',
      period: '/month',
      description: 'Essential business foundation',
      features: [
        'Business Entity Formation',
        'EIN Registration',
        'Registered Agent (1 Year)',
        'Basic Document Templates',
        'Email Support',
        '1 Strategy Call/month'
      ],
      popular: false,
      cta: 'Get Started'
    },
    {
      name: 'BLDR Pro',
      price: '199',
      period: '/month',
      description: 'Complete business development',
      features: [
        'Everything in Starter',
        'Financial Setup & Bookkeeping',
        'Marketing Foundation',
        'CRM Implementation',
        'Priority Support',
        '4 Strategy Calls/month'
      ],
      popular: true,
      cta: 'Go Pro'
    },
    {
      name: 'BLDR Elite',
      price: '299',
      period: '/month',
      description: 'Full-service acceleration',
      features: [
        'Everything in Pro',
        'Legal & Compliance Package',
        'Advanced Marketing Suite',
        'CFO Advisory Services',
        'Dedicated Account Manager',
        'Unlimited Strategy Calls'
      ],
      popular: false,
      cta: 'Go Elite'
    },
    {
      name: 'Enterprise',
      price: '399',
      period: '/month',
      description: 'Custom solutions for scale',
      features: [
        'Everything in Elite',
        'White-Label Options',
        'Custom Integrations',
        'Multi-Location Support',
        'API Access',
        'Dedicated Team'
      ],
      popular: false,
      cta: 'Contact Sales'
    }
  ];

  const oneTimeServices = [
    {
      name: 'Catalyst Full Analysis',
      price: '$199',
      description: '9-dimension business analysis with AI-powered recommendations, 90-day sprint, and 12-month roadmap',
      route: '/catalyst'
    },
    {
      name: 'Catalyst Single Problem',
      price: '$49',
      description: 'Focused analysis on one specific business challenge with actionable solutions',
      route: '/catalyst'
    },
    {
      name: 'Interview Lite',
      price: '$99',
      description: 'AI-guided business assessment with personalized recommendations',
      route: '/interview'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-6 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Pricing & Plans</h1>
              <p className="text-blue-200 text-sm">Choose the right plan for your business</p>
            </div>
          </div>
          <a
            href={BLDR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold rounded-lg text-sm transition-colors"
          >
            BLDR Pro Portal
          </a>
        </div>
      </header>

      {/* Monthly Subscription Plans */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-900 text-white">Monthly Plans</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Subscription Plans
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ongoing support and services with predictable monthly pricing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {monthlyPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative bg-white overflow-hidden ${
                  plan.popular 
                    ? 'border-2 border-blue-900 shadow-xl scale-105' 
                    : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500"></div>
                )}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500 text-white px-4 py-1 border-0">
                      <Star className="w-3 h-3 mr-1" /> Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 pt-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-blue-900 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-blue-900 hover:bg-blue-800 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => {
                      if (plan.name === 'Enterprise') {
                        window.location.href = 'tel:5104224451';
                      } else {
                        window.open(BLDR_URL, '_blank');
                      }
                    }}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* One-Time Services */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-500 text-white">One-Time Services</Badge>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Pay-Per-Use Options
            </h2>
            <p className="text-lg text-gray-600">
              Get specific help without a monthly commitment
            </p>
          </div>

          <div className="space-y-4">
            {oneTimeServices.map((service, idx) => (
              <Card key={idx} className="border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <CardContent className="p-6 flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{service.name}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </div>
                  <div className="flex items-center gap-4 ml-6">
                    <span className="text-2xl font-bold text-blue-900">{service.price}</span>
                    <Button 
                      onClick={() => navigate(service.route)}
                      className="bg-blue-900 hover:bg-blue-800 text-white"
                    >
                      Get Started <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Advisory Services Note */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <Zap className="w-12 h-12 text-purple-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Need Expert Advice On-Demand?
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            Advizzu Advisory Services offer pay-per-minute consultations with experts in 
            marketing, legal, compliance, and business strategy.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/services')}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            View Advisory Services
          </Button>
          <p className="mt-4 text-sm text-gray-500">
            Rates from $2.49 to $4.99 per minute
          </p>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-6 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Phone className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4">
            Not Sure Which Plan is Right?
          </h2>
          <p className="text-blue-100 mb-6">
            Speak with our team to find the perfect solution for your business needs.
          </p>
          <Button 
            size="lg"
            variant="outline"
            className="border-white text-white hover:bg-white/10"
            onClick={() => window.location.href = 'tel:5104224451'}
          >
            Call (510) 422-4451
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm">© 2025 Companeze. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
